// State for the /admin lexicon propagation bar. Kept separate from the
// component because the web suite runs in `environment: "node"` — this is
// where the behaviour that can actually be wrong gets tested.

export type Counts = {
  insert: number;
  update: number;
  deactivate: number;
  skipped_collision: number;
};

export type Propagation = {
  target: number;
  caught_up: number;
  total: number;
  state: "pending" | "done" | "degraded";
};

export const PROPAGATION_POLL_MS = 2000;

export function propagationPercent(status: Propagation | null): number {
  if (!status || status.total <= 0) return 0;
  return Math.round((status.caught_up / status.total) * 100);
}

export function propagationLabel(status: Propagation | null): string {
  if (!status) return "Pods bijwerken…";
  if (status.state === "done") return "Live op alle pods.";
  if (status.state === "degraded") {
    return (
      "De wijziging is opgeslagen in de database, maar nog niet live op alle pods: " +
      "Valkey is onbereikbaar of een pod loopt achter. Een herstart van de backend " +
      "maakt hem alsnog actief."
    );
  }
  return `${status.caught_up} van ${status.total} pods bijgewerkt`;
}

export function shouldKeepPolling(epoch: number, status: Propagation | null): boolean {
  if (!epoch) return false;
  return status === null || status.state === "pending";
}

// `bump()` on the backend returns epoch 0 when Valkey is unreachable — a
// real value, not a "nothing happened" sentinel. A commit can succeed (rows
// written, counts returned) with epoch 0, and there is then no epoch to
// poll `/propagation` for (shouldKeepPolling already refuses that case).
// The component must still show something conclusive rather than nothing,
// so it renders this synthetic degraded status instead of the real poll
// result — reusing propagationLabel's existing degraded wording rather
// than inventing a second "no epoch" message.
export function effectivePropagation(
  epoch: number,
  status: Propagation | null,
): Propagation | null {
  if (epoch <= 0) {
    return { target: 0, caught_up: 0, total: 0, state: "degraded" };
  }
  return status;
}
